import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Shell from "../../components/Shell";
import api, { formatApiError } from "../../lib/api";
import { StatusPill } from "../clinic/Dashboard";
import { toast } from "sonner";

const STATUS_TABS = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "all", label: "All" },
];

export default function AdminClinics() {
    const [params, setParams] = useSearchParams();
    const initial = params.get("status") || "pending";
    const [tab, setTab] = useState(initial);
    const [list, setList] = useState([]);
    const [selected, setSelected] = useState(null);
    const [note, setNote] = useState("");
    const [salesNote, setSalesNote] = useState("");

    const reload = async () => {
        const url = tab === "all" ? "/admin/clinics" : `/admin/clinics?status=${tab}`;
        const { data } = await api.get(url);
        setList(data || []);
    };
    useEffect(() => { reload(); setParams(tab === "pending" ? {} : { status: tab }); /* eslint-disable-next-line */ }, [tab]);

    const approve = async (c) => {
        try {
            await api.post(`/admin/clinics/${c.id}/approve`, { note });
            toast.success(`${c.clinic_name} approved`);
            setNote(""); setSelected(null); reload();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const reject = async (c) => {
        try {
            await api.post(`/admin/clinics/${c.id}/reject`, { note });
            toast.success(`${c.clinic_name} rejected`);
            setNote(""); setSelected(null); reload();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const addSalesNote = async (c) => {
        if (!salesNote.trim()) return;
        try {
            await api.post(`/admin/clinics/${c.id}/sales-notes`, { note: salesNote });
            setSalesNote("");
            const fresh = await api.get(`/admin/clinics/${c.id}`);
            setSelected(fresh.data);
            toast.success("Note added");
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const sendPasswordReset = async (c) => {
        if (!window.confirm(`Send a password reset link to ${c.email}?`)) return;
        try {
            const { data } = await api.post(`/admin/clinics/${c.id}/send-password-reset`);
            toast.success(`Reset link emailed to ${data.email}`);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <Shell variant="admin">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 animate-fade-in">
                <p className="eyebrow mb-2">Customer management</p>
                <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight mb-8">Clinics</h1>

                <div className="flex flex-wrap gap-2 mb-6">
                    {STATUS_TABS.map(t => (
                        <button key={t.value} onClick={() => setTab(t.value)} data-testid={`clinics-tab-${t.value}`} className={`text-xs uppercase tracking-widest px-3 py-2 border rounded-sm ${tab === t.value ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-200 hover:border-black"}`}>{t.label}</button>
                    ))}
                </div>

                <div className="card-base overflow-hidden" data-testid="clinics-list">
                    <table className="w-full text-left">
                        <thead className="bg-zinc-50/60 border-b border-zinc-200">
                            <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                                <th className="py-3 px-4">Clinic</th><th className="py-3 px-4">Contact</th><th className="py-3 px-4">Email</th><th className="py-3 px-4">Status</th><th className="py-3 px-4">Submitted</th><th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map(c => (
                                <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                                    <td className="py-3 px-4 text-sm font-medium">{c.clinic_name}</td>
                                    <td className="py-3 px-4 text-sm text-zinc-600">{c.contact_name}</td>
                                    <td className="py-3 px-4 text-sm text-zinc-600">{c.email}</td>
                                    <td className="py-3 px-4"><StatusPill status={c.status} /></td>
                                    <td className="py-3 px-4 text-xs text-zinc-500">{new Date(c.created_at).toLocaleDateString()}</td>
                                    <td className="py-3 px-4 text-right">
                                        <button data-testid={`clinic-open-${c.id}`} onClick={() => setSelected(c)} className="btn-ghost">Open →</button>
                                    </td>
                                </tr>
                            ))}
                            {list.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-sm text-zinc-500">No clinics in this view.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {selected && (
                <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setSelected(null)}>
                    <div className="bg-white w-full max-w-xl h-full overflow-y-auto p-8 animate-fade-in" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between mb-6">
                            <div>
                                <p className="eyebrow mb-1">Clinic application</p>
                                <h2 className="font-heading text-2xl">{selected.clinic_name}</h2>
                            </div>
                            <StatusPill status={selected.status} />
                        </div>

                        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm mb-8">
                            <Detail label="Trading name" value={selected.trading_name} />
                            <Detail label="NZBN" value={selected.nzbn} />
                            <Detail label="Website" value={selected.website} />
                            <Detail label="Instagram" value={selected.instagram} />
                            <Detail label="Phone" value={selected.phone} />
                            <Detail label="Mobile" value={selected.mobile} />
                            <Detail label="Address" value={selected.address} colspan />
                            <Detail label="Contact" value={`${selected.contact_name} · ${selected.position || ""}`} colspan />
                            <Detail label="Email" value={selected.email} />
                            <Detail label="Professional type" value={selected.professional_type} />
                            <Detail label="Registration #" value={selected.registration_number} />
                            <Detail label="Brand interests" value={(selected.brand_interests || []).join(", ")} colspan />
                            <Detail label="Applicant notes" value={selected.notes} colspan />
                        </dl>

                        <div className="border-t border-zinc-200 pt-6">
                            <label className="input-label">Admin note (optional)</label>
                            <textarea data-testid="approval-note" value={note} onChange={(e) => setNote(e.target.value)} className="input-base min-h-[80px]" />
                            <div className="flex gap-3 mt-4 flex-wrap">
                                {selected.status !== "approved" && <button data-testid="approve-btn" onClick={() => approve(selected)} className="btn-primary">Approve clinic</button>}
                                {selected.status !== "rejected" && <button data-testid="reject-btn" onClick={() => reject(selected)} className="btn-secondary">Reject</button>}
                                {selected.status === "approved" && <button data-testid="send-password-reset-btn" onClick={() => sendPasswordReset(selected)} className="btn-secondary">Send password reset link</button>}
                                <button onClick={() => setSelected(null)} className="btn-ghost ml-auto">Close</button>
                            </div>
                        </div>

                        <div className="border-t border-zinc-200 pt-6 mt-8">
                            <p className="eyebrow mb-3">Sales notes</p>
                            <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                                {(selected.sales_notes || []).map(n => (
                                    <div key={n.id} className="text-sm bg-zinc-50 border border-zinc-200 p-3 rounded-sm">
                                        <p>{n.note}</p>
                                        <p className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
                                    </div>
                                ))}
                                {(!selected.sales_notes || selected.sales_notes.length === 0) && <p className="text-sm text-zinc-500">No notes yet.</p>}
                            </div>
                            <textarea data-testid="sales-note-input" value={salesNote} onChange={(e) => setSalesNote(e.target.value)} placeholder="Add internal sales note…" className="input-base min-h-[60px]" />
                            <button data-testid="sales-note-add" onClick={() => addSalesNote(selected)} className="btn-secondary mt-3">Add note</button>
                        </div>
                    </div>
                </div>
            )}
        </Shell>
    );
}

function Detail({ label, value, colspan }) {
    return (
        <div className={colspan ? "col-span-2" : ""}>
            <dt className="text-[10px] uppercase tracking-widest text-zinc-400 mb-0.5">{label}</dt>
            <dd className="text-zinc-800">{value || "—"}</dd>
        </div>
    );
}
